import { Button, useDialogContext, useStoreState } from "@ariakit/react";
import { groupBy, LetterheadSubmitError, useForm } from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import type { SpaceGits } from "@indietabletop/types";
import { type ReactNode } from "react";
import { toast } from "react-hot-toast";
import { database } from "../../database.ts";
import * as fmt from "../fmt.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import {
  FormOption,
  FormOptions,
  type FormOptionProps,
} from "../forms/FormTextField.tsx";
import { Counter } from "./Counter.tsx";
import { ItemToBeRemovedWarning } from "./FormNote.tsx";
import { useGitContext, useVehicleContext } from "./ModelContext.tsx";
import { PreviewBanner } from "./StashPreviewBanner.tsx";
import { formOption } from "./style.css.ts";
import { useCountsById } from "./useCountsById.ts";

type VehicleWeaponOptionProps = Pick<FormOptionProps, "name" | "onChange"> & {
  extras?: ReactNode;
  showExtras: boolean;
  count: number;
  label: string;
  value: string;
  stats: string;
  cost: number;
  rules: fmt.ListItem[];
};

function VehicleWeaponOption(props: VehicleWeaponOptionProps) {
  const { label, value, stats, count, extras, rules, cost, ...rest } = props;

  return (
    <FormOption
      {...rest}
      type="checkbox"
      value={value}
      label={label}
      info={fmt.caps(cost)}
      description={
        <>
          {stats}

          {rules.length > 0 && (
            <>
              <br />
              Special: {fmt.list(rules)}
            </>
          )}

          <div className={formOption.extras}>{extras}</div>
        </>
      }
    />
  );
}

type GitWeaponOptionProps = Pick<FormOptionProps, "name" | "onChange"> & {
  label: string;
  value: string;
  stats: string;
  hands: number;
  cost: number;
  rules: fmt.ListItem[];
  extras?: ReactNode;
};

function GitWeaponOption(props: GitWeaponOptionProps) {
  const { label, value, stats, hands, cost, rules, extras, ...rest } = props;

  return (
    <FormOption
      {...rest}
      type="checkbox"
      value={value}
      label={label}
      info={`${fmt.caps(cost)} · ${fmt.hands(hands)}`}
      description={
        <>
          {stats}

          {rules.length > 0 && (
            <>
              <br />
              Special: {fmt.list(rules)}
            </>
          )}

          <div className={formOption.extras}>{extras}</div>
        </>
      }
    />
  );
}

function toWeaponRefs(ids: string[], counts: Map<string, number>) {
  return ids.map((id) => ({ id, count: counts.get(id) ?? 1 }));
}

export function ChooseGitWeaponsDialog() {
  const git = useGitContext();
  const { itemCostLimit, weaponRefs, ruleset, gang } = git;
  const isFilteredList = itemCostLimit !== Infinity;
  const dialog = useDialogContext();
  const rulestWeapons = ruleset.weapons.toArray();
  const initialWeaponIds = weaponRefs.map((w) => w.id);
  const { isCampaignMode } = gang.mode;
  const costModifier = isCampaignMode
    ? ruleset.constants.campaignModeCostMultiplier
    : 1;

  const initialWeaponCounts = new Map(
    weaponRefs.map((ref) => [ref.id, ref.count]),
  );

  const weapons = isFilteredList
    ? rulestWeapons.filter(
        (w) => w.cost <= itemCostLimit || initialWeaponIds.includes(w.id),
      )
    : rulestWeapons;

  const weaponsByType = groupBy(weapons, (w) => {
    return w.isRanged ? "shooting" : "fighting";
  });

  const { counts, increment, decrement } = useCountsById(initialWeaponCounts);

  const { form, submitName } = useForm({
    defaultValues: {
      weaponIds: initialWeaponIds,
    },

    async onSubmit({ values }) {
      const weaponsRefs = toWeaponRefs(values.weaponIds, counts);
      const nextGit = git.addWeapons(weaponsRefs);
      return await database.gangs.updateOne(nextGit.gang.data);
    },

    onSuccess() {
      dialog?.hide();
      toast("Saved");
    },
  });

  const weaponIds = useStoreState(form, (s) => s.values.weaponIds);
  const draftGang = git.addWeapons(toWeaponRefs(weaponIds, counts)).gang;

  return (
    <FormDialog
      overline="Grandpa Gunnock's"
      heading="Weapons Catalogue"
      form={form}
    >
      <PreviewBanner where="in the shop" draftGang={draftGang} />

      {isFilteredList && (
        <p style={{ textAlign: "center", lineHeight: 1.4 }}>
          Only showing weapons available for this git&apos;s type and
          gang&apos;s build mode.
        </p>
      )}

      <FormOptions label="Shooting Weapons">
        {weaponsByType.shooting.map((w) => {
          const willBeLost =
            isCampaignMode &&
            initialWeaponIds.includes(w.id) &&
            !weaponIds.includes(w.id);

          const showCounter =
            w.cost <= itemCostLimit && weaponIds.includes(w.id);

          return (
            <GitWeaponOption
              key={w.id}
              name={form.names.weaponIds}
              label={w.name}
              value={w.id}
              stats={w.stats}
              cost={w.cost * costModifier}
              rules={w.rules}
              hands={w.hands}
              extras={
                <>
                  {showCounter && (
                    <Counter
                      count={counts.get(w.id) ?? 1}
                      onDecrement={() => void decrement(w.id)}
                      onIncrement={() => void increment(w.id)}
                    />
                  )}

                  {willBeLost && <ItemToBeRemovedWarning what="WEAPON" />}
                </>
              }
            />
          );
        })}
      </FormOptions>

      <FormOptions label="Fighting Weapons">
        {weaponsByType.fighting.map((w) => {
          const willBeLost =
            isCampaignMode &&
            initialWeaponIds.includes(w.id) &&
            !weaponIds.includes(w.id);

          const showCounter =
            w.cost <= itemCostLimit && weaponIds.includes(w.id);

          return (
            <GitWeaponOption
              key={w.id}
              name={form.names.weaponIds}
              label={w.name}
              value={w.id}
              stats={w.stats}
              cost={w.cost * costModifier}
              rules={w.rules}
              hands={w.hands}
              extras={
                <>
                  {showCounter && (
                    <Counter
                      count={counts.get(w.id) ?? 1}
                      onDecrement={() => void decrement(w.id)}
                      onIncrement={() => void increment(w.id)}
                    />
                  )}

                  {willBeLost && <ItemToBeRemovedWarning what="WEAPON" />}
                </>
              }
            />
          );
        })}
      </FormOptions>

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}

function toVehicleWeaponRefs(
  values: { fixedWeaponId: string; weaponIds: string[] },
  counts: Map<string, number>,
): SpaceGits.VehicleWeaponRef[] {
  return values.weaponIds.map((id) => {
    return {
      id,
      placement: values.fixedWeaponId === id ? "FIXED" : "PINTLE_MOUNTED",
      count: counts.get(id) ?? 1,
    };
  });
}

export function ChooseVehicleWeaponsDialog() {
  const vehicle = useVehicleContext();
  const { weaponRefs, weaponCostLimit, ruleset, gang } = vehicle;
  const initialWeaponIds = weaponRefs.map((ref) => ref.id);
  const isFilteredList = weaponCostLimit !== Infinity;
  const rulesetWeapons = ruleset.weapons.toArray().filter((w) => w.isRanged);
  const { isCampaignMode } = gang.mode;
  const costModifier = isCampaignMode
    ? ruleset.constants.campaignModeCostMultiplier
    : 1;
  const dialog = useDialogContext();

  const weapons = isFilteredList
    ? rulesetWeapons.filter(
        (w) =>
          w.cost <= vehicle.weaponCostLimit || initialWeaponIds.includes(w.id),
      )
    : rulesetWeapons;

  const { counts, increment, decrement } = useCountsById(
    new Map(vehicle.data.weapons.map((ref) => [ref.id, ref.count])),
  );

  const { form, submitName } = useForm({
    defaultValues: {
      fixedWeaponId:
        vehicle.data.weapons.find((w) => w.placement === "FIXED")?.id ?? "",
      weaponIds: vehicle.data.weapons.map((w) => w.id),
    },

    async onSubmit({ values }) {
      const weaponRefs = toVehicleWeaponRefs(values, counts);
      const nextVehicle = vehicle.addWeapons(weaponRefs);
      return await database.gangs.updateOne(nextVehicle.gang.data);
    },

    onSuccess() {
      dialog?.hide();
      toast("Saved");
    },
  });

  const values = useStoreState(form, (s) => s.values);
  const draftVehicle = vehicle.addWeapons(toVehicleWeaponRefs(values, counts));

  return (
    <FormDialog overline="Krankshaft's" heading="Weapons Catalogue" form={form}>
      <PreviewBanner where="in the shop" draftGang={draftVehicle.gang} />

      {isFilteredList && (
        <p style={{ textAlign: "center", lineHeight: 1.4 }}>
          Only showing weapons available for this gang&apos;s build mode.
        </p>
      )}

      <FormOptions label="Vehicle Weapons">
        {weapons.map((w) => {
          const handleWeaponToggle = () => {
            const selectedWeaponIds = form.getValue<string[]>(
              form.names.weaponIds,
            );

            const selectedFixedWeaponId = form.getValue<string>(
              form.names.fixedWeaponId,
            );

            // If we are selecting a weapon and no weapon is currently selected
            // as fixed positioning, select fixed positioning.
            if (!selectedWeaponIds.includes(w.id) && !selectedFixedWeaponId) {
              form.setValue(form.names.fixedWeaponId, w.id);
            }

            // If we are unselecting a selected weapon that is also marked as
            // fixed positioned, unselect the positioning too.
            if (
              selectedWeaponIds.includes(w.id) &&
              selectedFixedWeaponId === w.id
            ) {
              form.setValue(form.names.fixedWeaponId, "");
            }
          };

          const isSelected = values.weaponIds.includes(w.id);
          const showCounter = w.cost <= weaponCostLimit && isSelected;
          const willBeLost =
            isCampaignMode &&
            initialWeaponIds.includes(w.id) &&
            !values.weaponIds.includes(w.id);

          return (
            <VehicleWeaponOption
              key={w.id}
              cost={w.cost * costModifier}
              label={w.name}
              rules={w.rules}
              stats={w.stats}
              value={w.id}
              name={form.names.weaponIds}
              onChange={handleWeaponToggle}
              count={counts.get(w.id) ?? 1}
              showExtras={showCounter}
              extras={
                <>
                  {showCounter && (
                    <div className={formOption.extrasRow}>
                      <Counter
                        count={counts.get(w.id) ?? 1}
                        onDecrement={() => void decrement(w.id)}
                        onIncrement={() => void increment(w.id)}
                      />

                      <Button
                        className={interactiveText}
                        onClick={() =>
                          void form.setValue(
                            form.names.fixedWeaponId,
                            values.fixedWeaponId === w.id ? "" : w.id,
                          )
                        }
                      >
                        {values.fixedWeaponId === w.id
                          ? "Fixed forward"
                          : "Pintle mounted"}
                      </Button>
                    </div>
                  )}

                  {willBeLost && <ItemToBeRemovedWarning what="WEAPON" />}
                </>
              }
            />
          );
        })}
      </FormOptions>

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}
